import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Eye } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import ClientUsers from "@/pages/admin/ClientUsers";
import ClientSubscriptions from "@/pages/admin/ClientSubscriptions";
import ClientDocuments from "@/pages/admin/ClientDocuments";

const FIELDS = [["company_name", "Ragione sociale"], ["vat_number", "Partita IVA"], ["contact_name", "Referente"], ["phone", "Telefono"], ["address", "Indirizzo"], ["pec", "PEC"]];

function ClientInfo({ client, onSaved }) {
  const [f, setF] = useState(client);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { users, id, created_at, ...body } = f;
      const { data } = await api.put(`/admin/clients/${client.id}`, body);
      toast.success("Anagrafica salvata");
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Panel title="Anagrafica cliente">
      <form onSubmit={save} className="p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={f[k] || ""} required={k === "company_name"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`client-${k}-input`} /></div>
          ))}
        </div>
        <div className="space-y-1.5"><Label>Note interne</Label><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="client-notes-input" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="client-active-switch" />Cliente attivo (può accedere al portale)</label>
        <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="client-save-button">Salva anagrafica</Button>
      </form>
    </Panel>
  );
}

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [pages, setPages] = useState([]);
  const load = useCallback(() => api.get(`/admin/clients/${id}`).then((r) => setClient(r.data)), [id]);
  useEffect(() => {
    load();
    api.get("/pages").then((r) => setPages(r.data));
  }, [load]);
  if (!client) return null;
  const remove = async () => {
    if (!window.confirm(`Eliminare definitivamente ${client.company_name} con utenti, abbonamenti e documenti?`)) return;
    await api.delete(`/admin/clients/${id}`);
    toast.success("Cliente eliminato");
    navigate("/admin/clienti");
  };
  return (
    <div>
      <Link to="/admin/clienti" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 mb-4" data-testid="back-to-clients"><ArrowLeft size={14} />Clienti</Link>
      <PageHeader eyebrow={client.vat_number || "Cliente"} title={client.company_name}
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" className="border-amber-500/40 text-amber-300 bg-transparent hover:bg-amber-500/10" onClick={() => navigate(`/admin/clienti/${id}/vista`)} data-testid="view-as-client-button"><Eye size={15} className="mr-1.5" />Vedi come cliente</Button>
          <Button variant="outline" className="border-rose-500/40 text-rose-400 bg-transparent hover:bg-rose-500/10" onClick={remove} data-testid="delete-client-button"><Trash2 size={15} className="mr-1.5" />Elimina cliente</Button>
        </div>} />
      <Tabs defaultValue="subs">
        <TabsList className="mb-6 bg-slate-900 border border-slate-800">
          <TabsTrigger value="subs" data-testid="tab-subscriptions">Abbonamenti</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-documents">Documenti</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Utenti di accesso ({client.users.length})</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">Anagrafica</TabsTrigger>
        </TabsList>
        <TabsContent value="subs"><ClientSubscriptions clientId={id} pages={pages} companyName={client.company_name} /></TabsContent>
        <TabsContent value="docs"><ClientDocuments clientId={id} pages={pages} /></TabsContent>
        <TabsContent value="users"><ClientUsers clientId={id} users={client.users} onChange={load} /></TabsContent>
        <TabsContent value="info"><ClientInfo client={client} onSaved={setClient} /></TabsContent>
      </Tabs>
    </div>
  );
}
